import { useEffect, useState } from 'react'

import {
  getFdWindowMs,
  useFdActiveGroup,
} from '@/buyer-portal/components/features/listing/hooks/useFdActiveGroup'
import type { FirstDibsWithWindowStart } from '@/queries/listingDetailsQuery'

const secondsUntil = (targetMs: number) =>
  Math.max(0, Math.floor((targetMs - Date.now()) / 1000))

// targetMs is a stable number so the effect only restarts when the window actually changes.
const useCountdownSeconds = (targetMs: number): number => {
  const [remaining, setRemaining] = useState(() => secondsUntil(targetMs))
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setRemaining(secondsUntil(targetMs))
    if (targetMs <= 0) return
    const id = setInterval(() => setRemaining(secondsUntil(targetMs)), 1000)
    return () => clearInterval(id)
  }, [targetMs])
  return remaining
}

export const formatCountdown = (totalSeconds: number): string => {
  const d = Math.floor(totalSeconds / 86400)
  const h = Math.floor((totalSeconds % 86400) / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  if (d > 0) return `${d}d ${h}h ${m}m ${s}s`
  if (h > 0) return `${h}h ${m}m ${s}s`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

/**
 * Seconds left in the buyer's active First Dibs window, or 0 when the flag is
 * off or no window is open. Uses the same active group as the bid card.
 */
export const useFirstDibsCountdown = (
  firstDibs: FirstDibsWithWindowStart | undefined
): number => {
  const { activeGroup } = useFdActiveGroup(firstDibs)
  const windowEndMs = activeGroup ? (getFdWindowMs(activeGroup)?.endMs ?? 0) : 0
  const remaining = useCountdownSeconds(windowEndMs)
  return activeGroup ? remaining : 0
}
